import i18n from "../../../i18n";import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ChevronRight, ClipboardList } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, EmptyState, PageHeader, ui } from "../../../components/ui";
import { getSubmoduleFeatures, cleanName } from "../../../router/navigation";
import { getServiceMeta } from "../../../utils/serviceColors";

/**
 * Generic fallback page for any enabledServices submodule that doesn't have
 * a dedicated page yet (see router/navigation.js -> ROUTE_LINKS). It renders
 * the submodule's feature list from the session tree inside the standard
 * AppLayout shell, so every sidebar entry lands on a real routed page.
 *
 * When a submodule gets its own implementation, add its route to
 * AppRoutes.jsx and map it in ROUTE_LINKS — this page then simply stops
 * being used for it.
 */
export function SubmoduleFeaturesPage() {
  const { service, module, submodule } = useParams();
  const meta = getServiceMeta(service);
  const features = getSubmoduleFeatures(service, module, submodule);

  const [toast, setToast] = useState("");
  const showToast = useCallback((text) => {
    setToast(text);
    setTimeout(() => setToast(""), 2500);
  }, []);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto w-full">

        <PageHeader back={<PageBackLink to="/" label="Dashboard" />} title={cleanName(submodule)} />

        <ContentCard>
          {features.length === 0 ?
          <EmptyState
            icon={ClipboardList}
            title={i18n.t("serviceExplorer.noFeaturesFound")}
            message="This submodule has no features enabled for your account." /> :


          features.map((f) =>
          <button
            key={f.code || f.feature}
            type="button"
            className="w-full px-4 sm:px-5 py-3.5 flex items-center justify-between border-b border-(--color-text-border)/50 last:border-b-0 hover:bg-workspace/30 transition text-left cursor-pointer"
            onClick={() => showToast(`${f.code} · ${f.api}`)}>
            
                <div className="flex items-center gap-3">
                  <div
                className="h-10 w-10 rounded-xl grid place-items-center shrink-0"
                style={{ background: meta.bg, color: meta.color }}>
                
                    <ClipboardList className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{f.feature}</p>
                    <p className={`${ui.pageSub} font-mono text-xs`}>
                      {f.code} · {f.api}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-(--color-text-border) shrink-0" />
              </button>
          )
          }
        </ContentCard>
      </div>

      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-500 bg-foreground text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg transition-opacity pointer-events-none whitespace-nowrap ${
        toast ? "opacity-100" : "opacity-0"}`
        }
        role="status"
        aria-live="polite">
        
        {toast}
      </div>
    </AppLayout>);

}
